import {
  forwardRef,
  ForwardRefExoticComponent,
  MouseEventHandler,
  PropsWithChildren,
  PropsWithoutRef,
  ReactNode,
  RefAttributes,
} from 'react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import GoogleLogo from '@siloreal:assets:logo/integrations-logos/google-logo.svg';
import MicrosoftLogo from '@siloreal:assets:logo/integrations-logos/microsoft-logo.svg';
import { Link as RouterLink } from 'react-router';
import { CircularProgress } from '../progress/circular';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type SolidVariant =
  | 'primary'
  | 'alternative'
  | 'danger'
  | 'major'
  | 'minor'
  | 'spotlight'
  | 'safe'
  | 'text-link'
  | 'outlined';
type HollowVariant = 'primary-hollow' | 'alternative-hollow';
export type AuthProvider = 'google' | 'microsoft';

type SecondaryButtonTone = 'danger' | 'alternative';
type ButtonSize = 'small' | 'medium' | 'large';

type SecondaryButtonConfig = {
  active: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  icon?: ReactNode;
  label?: ReactNode;
  tone?: SecondaryButtonTone;
  disabled?: boolean;
  tid?: string;
  ariaLabel?: string;
};

export type ButtonIconConfig = {
  value: ReactNode;
  position?: 'start' | 'end';
  only?: boolean;
};

export type ButtonIcon = ReactNode | ButtonIconConfig;

type IconWithLabel = ReactNode | (ButtonIconConfig & { only?: false | undefined });
type IconOnly = ButtonIconConfig & { only: true };

type CommonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  elevation?: boolean;
  uppercase?: boolean;
  size?: ButtonSize;
  noPadding?: boolean;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  navigate?: string;
  secondaryButton?: SecondaryButtonConfig;
  tid?: string;
} & PropsWithChildren;

type AuthProps = CommonProps & {
  variant: 'auth';
  provider: AuthProvider;
  icon?: never;
  removeBorder?: never;
  secondaryButton?: never;
};

type StandardCommonProps =
  | (CommonProps & {
      provider?: never;
      icon?: IconWithLabel;
    })
  | (CommonProps & {
      provider?: never;
      icon: IconOnly;
      ariaLabel: string;
    });

type StandardSolidProps = StandardCommonProps & {
  variant: SolidVariant;
  removeBorder?: never;
};

type StandardHollowProps = StandardCommonProps & {
  variant: HollowVariant;
  removeBorder?: boolean;
};

type StandardProps = StandardSolidProps | StandardHollowProps;

export type Props = StandardProps | AuthProps;

type VariantConfig = {
  muiVariant: 'contained' | 'outlined' | 'text';
  disableRipple?: boolean;
  disableElevation?: boolean;
  sx?: React.ComponentProps<typeof MuiButton>['sx'];
};

type SecondaryToneStyleConfig = {
  borderColor: string;
  textColor: string;
  backgroundColor: string;
  hoverBorderColor: string;
  hoverBackgroundColor: string;
};

type IconOnlyStyleConfig = {
  color: string;
  hoverBackgroundColor: string;
};

const VARIANT_CONFIG: Record<Props['variant'], VariantConfig> = {
  primary: {
    muiVariant: 'contained',
    sx: {
      backgroundColor: '#6BAA00',
      color: '#fff',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#5a9400',
      },
    },
  },
  'primary-hollow': {
    muiVariant: 'outlined',
    sx: {
      borderColor: '#6BAA00',
      color: '#6BAA00',
      textTransform: 'uppercase',
      fontWeight: 600,
      '&:hover': {
        borderColor: '#5a9400',
        backgroundColor: 'rgba(107, 170, 0, 0.04)',
      },
    },
  },
  'text-link': {
    muiVariant: 'text',
    disableRipple: true,
    sx: {
      display: 'inline-flex',
      alignSelf: 'center',
      width: 'max-content',
      height: 'max-content',
      maxWidth: 'max-content',
      maxHeight: 'max-content',
      backgroundColor: 'transparent',
      color: '#6BAA00',
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 400,
      padding: '6px 8px',
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      },
      '&:active': {
        backgroundColor: 'transparent',
        textShadow: '0 0 8px rgba(107, 170, 0, 0.4)',
        transform: 'scale(1.04)',
      },
    },
  },
  outlined: {
    muiVariant: 'outlined',
  },
  alternative: {
    muiVariant: 'contained',
    sx: {
      backgroundColor: '#22A7F0',
      color: '#fff',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#1C93D3',
      },
    },
  },
  danger: {
    muiVariant: 'contained',
    disableElevation: true,
    sx: {
      backgroundColor: '#fbd1d1',
      color: '#ea1818',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#fbd1d1',
      },
    },
  },
  major: {
    muiVariant: 'contained',
    disableElevation: true,
    sx: {
      backgroundColor: '#a85ce3',
      color: '#ffffff',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#a85ce3',
      },
    },
  },
  minor: {
    muiVariant: 'contained',
    disableElevation: true,
    sx: {
      backgroundColor: '#49454f',
      color: '#ffffff',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#49454f',
      },
    },
  },
  spotlight: {
    muiVariant: 'contained',
    disableElevation: true,
    sx: {
      backgroundColor: '#d9f6ea',
      color: '#42d296',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#d9f6ea',
      },
    },
  },
  safe: {
    muiVariant: 'contained',
    disableElevation: true,
    sx: {
      backgroundColor: '#e1eecc',
      color: '#73bd00',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#e1eecc',
      },
    },
  },
  'alternative-hollow': {
    muiVariant: 'outlined',
    sx: {
      borderColor: '#22A7F0',
      color: '#22A7F0',
      textTransform: 'uppercase',
      fontWeight: 600,
      '&:hover': {
        borderColor: '#1C93D3',
        backgroundColor: 'rgba(34, 167, 240, 0.08)',
      },
    },
  },
  auth: {
    muiVariant: 'outlined',
    sx: {
      borderColor: '#d0d7de',
      color: '#1f2328',
      textTransform: 'none',
      fontWeight: 400,
      backgroundColor: '#fff',
      '&:hover': {
        borderColor: '#bec6cf',
        backgroundColor: '#f6f8fa',
      },
    },
  },
};

const SECONDARY_TONE_STYLE: Record<SecondaryButtonTone, SecondaryToneStyleConfig> = {
  danger: {
    borderColor: '#fbd1d1',
    textColor: '#ea1818',
    backgroundColor: '#fbd1d1',
    hoverBorderColor: '#f7c2c2',
    hoverBackgroundColor: '#f7c2c2',
  },
  alternative: {
    borderColor: '#22A7F0',
    textColor: '#ffffff',
    backgroundColor: '#22A7F0',
    hoverBorderColor: '#1C93D3',
    hoverBackgroundColor: '#1C93D3',
  },
};

const DEFAULT_ICON_ONLY_STYLE: IconOnlyStyleConfig = {
  color: '#6b7280',
  hoverBackgroundColor: 'rgba(107, 114, 128, 0.12)',
};

const SIZE_SX: Record<ButtonSize, React.ComponentProps<typeof MuiButton>['sx']> = {
  small: {
    minWidth: 0,
    fontSize: '0.8rem',
    lineHeight: 1.2,
    padding: '2px 4px',
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: 0.5,
      '& > *:nth-of-type(1)': {
        fontSize: 12,
      },
    },
    '& .MuiButton-endIcon': {
      marginLeft: 0.5,
      marginRight: 0,
      '& > *:nth-of-type(1)': {
        fontSize: 12,
      },
    },
  },
  medium: {},
  large: {
    fontSize: 16,
    padding: '8px 20px',
  },
};

const ICON_ONLY_STYLE_BY_VARIANT: Partial<Record<Props['variant'], IconOnlyStyleConfig>> = {
  primary: {
    color: '#6BAA00',
    hoverBackgroundColor: 'rgba(107, 170, 0, 0.12)',
  },
  'primary-hollow': {
    color: '#6BAA00',
    hoverBackgroundColor: 'rgba(107, 170, 0, 0.12)',
  },
  alternative: {
    color: '#22A7F0',
    hoverBackgroundColor: 'rgba(34, 167, 240, 0.12)',
  },
  'alternative-hollow': {
    color: '#22A7F0',
    hoverBackgroundColor: 'rgba(34, 167, 240, 0.12)',
  },
  danger: {
    color: '#ea1818',
    hoverBackgroundColor: 'rgba(234, 24, 24, 0.1)',
  },
};

const AUTH_PROVIDER_ICON: Record<AuthProvider, ReactNode> = {
  google: <img src={GoogleLogo} alt="" aria-hidden width={18} height={18} style={{ display: 'block' }} />,
  microsoft: <img src={MicrosoftLogo} alt="" aria-hidden width={18} height={18} style={{ display: 'block' }} />,
};

const isIconConfig = (icon: ButtonIcon | undefined): icon is ButtonIconConfig => {
  if (!icon || typeof icon !== 'object' || Array.isArray(icon)) return false;

  return 'value' in icon;
};

const normalizeIcon = (
  icon: ButtonIcon | undefined
): { value: ReactNode; position: 'start' | 'end'; only: boolean } | undefined => {
  if (!icon) return undefined;

  if (isIconConfig(icon)) {
    return {
      value: icon.value,
      position: icon.position ?? 'start',
      only: icon.only === true,
    };
  }

  return {
    value: icon,
    position: 'start',
    only: false,
  };
};

export const Button: FR<HTMLButtonElement, Props> = forwardRef((props: Props, ref) => {
  const {
    children,
    variant,
    provider,
    icon,
    removeBorder,
    onClick,
    disabled,
    loading = false,
    fullWidth,
    elevation = false,
    uppercase = false,
    size = 'medium',
    noPadding = false,
    type = 'button',
    ariaLabel,
    navigate,
    secondaryButton,
    tid,
  } = props;

  const hasSecondaryButton = Boolean(secondaryButton?.active);
  const isDisabled = Boolean(disabled || loading);
  const variantConfig = VARIANT_CONFIG[variant];
  const iconConfig = normalizeIcon(icon);
  const iconOnlyStyle = ICON_ONLY_STYLE_BY_VARIANT[variant] ?? DEFAULT_ICON_ONLY_STYLE;
  const hasContent = children !== undefined && children !== null;
  const isHollowVariant = variant === 'primary-hollow' || variant === 'alternative-hollow';
  const baseButtonStyle = { borderRadius: hasSecondaryButton ? '9999px 0 0 9999px' : '9999px' };
  const buttonStyle =
    isHollowVariant && removeBorder ? { ...baseButtonStyle, border: 'none' } : baseButtonStyle;
  const resolvedDisableElevation = !elevation;
  const resolvedSx: React.ComponentProps<typeof MuiButton>['sx'] = {
    ...(variantConfig.sx as Record<string, unknown>),
    ...(SIZE_SX[size] as Record<string, unknown>),
    textTransform: uppercase ? 'uppercase' : 'none',
    ...(noPadding
      ? {
          minWidth: 0,
          width: 'fit-content',
          alignSelf: 'flex-start',
          padding: 0,
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: 0.5,
            ...(size === 'small'
              ? {
                  '& > *:nth-of-type(1)': {
                    fontSize: 12,
                  },
                }
              : {}),
          },
          '& .MuiButton-endIcon': {
            marginLeft: 0.5,
            marginRight: 0,
            ...(size === 'small'
              ? {
                  '& > *:nth-of-type(1)': {
                    fontSize: 12,
                  },
                }
              : {}),
          },
        }
      : {}),
    ...(hasSecondaryButton && fullWidth ? { flex: 1 } : {}),
    ...(iconConfig?.only
      ? {
          minWidth: 0,
          width: 36,
          height: 36,
          padding: 0,
          borderRadius: '50%',
          border: 'none',
          color: iconOnlyStyle.color,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: iconOnlyStyle.hoverBackgroundColor,
            border: 'none',
          },
        }
      : {}),
  };

  const navigationProps = navigate
    ? {
        component: RouterLink,
        to: navigate,
      }
    : {};

  if (variant === 'auth') {
    const providerLabel = provider === 'microsoft' ? 'Microsoft' : 'Google';
    const authLabel = children ?? `Continuar con ${providerLabel}`;

    return (
      <MuiButton
        ref={ref}
        variant={variantConfig.muiVariant}
        startIcon={AUTH_PROVIDER_ICON[provider]}
        onClick={onClick}
        disabled={isDisabled}
        fullWidth={fullWidth}
        type={type}
        data-testid={tid}
        aria-label={ariaLabel}
        disableRipple={variantConfig.disableRipple}
        disableElevation={resolvedDisableElevation}
        sx={resolvedSx}
        style={baseButtonStyle}
        {...navigationProps}
      >
        <CircularProgress loading={loading} center={false} size={18}>
          {authLabel}
        </CircularProgress>
      </MuiButton>
    );
  }

  const iconProps =
    iconConfig && !iconConfig.only && hasContent
      ? iconConfig.position === 'end'
        ? { endIcon: iconConfig.value }
        : { startIcon: iconConfig.value }
      : {};

  const content = (
    <CircularProgress loading={loading} center={false} size={18}>
      {iconConfig?.only ? iconConfig.value : hasContent ? children : iconConfig?.value}
    </CircularProgress>
  );

  const mainButton = (
    <MuiButton
      ref={ref}
      variant={variantConfig.muiVariant}
      onClick={onClick}
      disabled={isDisabled}
      fullWidth={hasSecondaryButton ? false : fullWidth}
      type={type}
      data-testid={tid}
      aria-label={ariaLabel}
      disableRipple={variantConfig.disableRipple}
      disableElevation={resolvedDisableElevation}
      sx={resolvedSx}
      style={buttonStyle}
      {...iconProps}
      {...navigationProps}
    >
      {content}
    </MuiButton>
  );

  if (!hasSecondaryButton) {
    return mainButton;
  }

  const tone = secondaryButton?.tone ?? 'danger';
  const toneStyle = SECONDARY_TONE_STYLE[tone];
  const secondaryHasLabel = Boolean(secondaryButton?.label);
  const secondaryContent = secondaryHasLabel ? secondaryButton?.label : secondaryButton?.icon;
  const secondaryIconProps =
    secondaryButton?.icon && secondaryHasLabel ? { startIcon: secondaryButton.icon } : {};
  const secondaryDisabled = Boolean(isDisabled || secondaryButton?.disabled);

  return (
    <Box
      sx={{
        display: fullWidth ? 'flex' : 'inline-flex',
        alignItems: 'stretch',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {mainButton}

      <MuiButton
        variant="contained"
        onClick={secondaryButton?.onClick}
        disabled={secondaryDisabled}
        data-testid={secondaryButton?.tid}
        aria-label={secondaryButton?.ariaLabel}
        disableElevation={resolvedDisableElevation}
        sx={{
          minWidth: secondaryHasLabel ? undefined : 42,
          px: secondaryHasLabel ? 1.25 : 1,
          ml: '-1px',
          borderRadius: '0 9999px 9999px 0',
          border: `1px solid ${toneStyle.borderColor}`,
          color: toneStyle.textColor,
          backgroundColor: toneStyle.backgroundColor,
          textTransform: uppercase ? 'uppercase' : 'none',
          '&:hover': {
            borderColor: toneStyle.hoverBorderColor,
            backgroundColor: toneStyle.hoverBackgroundColor,
          },
        }}
        {...secondaryIconProps}
      >
        {secondaryContent}
      </MuiButton>
    </Box>
  );
});
